/* Marker coordinates follow the source photograph through responsive crops. */
if (!customElements.get('mgp-hero-hotspots')) {
  customElements.define('mgp-hero-hotspots', class extends HTMLElement {
    connectedCallback() {
      this.slide = this.closest('.slideshow__slide');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.update = this.update.bind(this);
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this);
      this.slideObserver = new MutationObserver(() => this.syncFocus());
      this.slideObserver.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
      this.hoverQuery = matchMedia('(any-hover: hover) and (any-pointer: fine)');
      this.addEventListener('pointerover', event => {
        if (event.pointerType !== 'mouse' || !this.hoverQuery.matches) return;
        const target = event.target.closest('.mgp-hero-hotspot__point, .mgp-hero-product-card');
        if (target) this.open(target.closest('.mgp-hero-hotspot'));
      }, options);
      this.addEventListener('pointerout', event => {
        if (event.pointerType !== 'mouse' || !this.hoverQuery.matches) return;
        const marker = event.target.closest('.mgp-hero-hotspot');
        if (!marker || marker.contains(event.relatedTarget)) return;
        clearTimeout(this.closeTimer);
        // Allow crossing the gap between point and product card without flicker.
        this.closeTimer = setTimeout(() => {
          if (this.openMarker === marker && !marker.contains(document.activeElement)) this.close();
        }, 260);
      }, options);
      this.addEventListener('focusin', event => {
        const point = event.target.closest('.mgp-hero-hotspot__point');
        if (point && point.matches(':focus-visible') && !this.restoringFocus && !this.pointerFocusing) this.open(point.closest('.mgp-hero-hotspot'));
      }, options);
      this.addEventListener('focusout', event => {
        const marker = event.target.closest('.mgp-hero-hotspot');
        if (marker && !marker.contains(event.relatedTarget)) this.close();
      }, options);
      ['pointerdown', 'touchstart', 'mousedown'].forEach(type => {
        this.addEventListener(type, event => {
          this.pointerFocusing = true;
          event.stopPropagation();
        }, options);
      });
      document.addEventListener('pointercancel', () => { this.pointerFocusing = false; }, options);
      document.addEventListener('touchcancel', () => { this.pointerFocusing = false; }, options);
      document.addEventListener('keydown', () => { this.pointerFocusing = false; }, options);
      this.addEventListener('click', event => {
        const point = event.target.closest('.mgp-hero-hotspot__point');
        const close = event.target.closest('.mgp-hero-product-card__close');
        if (point) {
          const wasOpen = point.getAttribute('aria-expanded') === 'true';
          if (wasOpen && !(event.pointerType === 'mouse' && this.hoverQuery.matches)) this.close();
          else this.open(point.closest('.mgp-hero-hotspot'));
          event.stopPropagation();
        } else if (close) { this.close(true); event.stopPropagation(); }
        this.pointerFocusing = false;
      }, options);
      document.addEventListener('click', event => {
        this.pointerFocusing = false;
        if (!this.contains(event.target)) this.close();
      }, options);
      document.addEventListener('keydown', event => { if (event.key === 'Escape' && this.openMarker) this.close(true); }, options);
      this.slide.querySelectorAll('img').forEach(image => image.addEventListener('load', this.update, options));
      this.syncFocus(); this.update();
    }
    disconnectedCallback() { clearTimeout(this.closeTimer); this.resizeObserver?.disconnect(); this.slideObserver?.disconnect(); this.abort?.abort(); }
    open(marker) {
      clearTimeout(this.closeTimer);
      if (!this.slide.classList.contains('is-selected') || marker.querySelector('button').hidden) return;
      if (this.openMarker === marker) return;
      this.close();
      this.openMarker = marker;
      marker.querySelector('button').setAttribute('aria-expanded', 'true');
      marker.querySelector('.mgp-hero-product-card').hidden = false;
      this.syncFocus(); this.update();
      this.dispatchEvent(new CustomEvent('mgp:hero-card', { bubbles: true }));
    }
    close(restoreFocus = false) {
      clearTimeout(this.closeTimer);
      if (!this.openMarker) return;
      const button = this.openMarker.querySelector('button');
      button.setAttribute('aria-expanded', 'false');
      this.openMarker.querySelector('.mgp-hero-product-card').hidden = true;
      this.openMarker = null;
      this.dispatchEvent(new CustomEvent('mgp:hero-card', { bubbles: true }));
      if (restoreFocus) {
        this.restoringFocus = true; button.focus(); this.restoringFocus = false;
      }
    }
    syncFocus() {
      const active = this.slide.classList.contains('is-selected');
      if (!active) this.close();
      this.querySelectorAll('button,a').forEach(control => { control.tabIndex = active ? 0 : -1; });
    }
    update() {
      const width = this.clientWidth, height = this.clientHeight;
      if (!width || !height) return;
      const image = [...this.slide.querySelectorAll('.slideshow__image')].find(img => img.getBoundingClientRect().width > 0);
      if (!image) return;
      const sourceWidth = image.naturalWidth || Number(this.dataset.imageWidth);
      const sourceHeight = image.naturalHeight || Number(this.dataset.imageHeight);
      if (!sourceWidth || !sourceHeight) return;
      const box = image.getBoundingClientRect(), host = this.getBoundingClientRect(), style = getComputedStyle(image);
      const scale = (style.objectFit === 'contain' ? Math.min : Math.max)(box.width / sourceWidth, box.height / sourceHeight);
      const position = style.objectPosition.split(' ').map(Number.parseFloat);
      const left = box.left - host.left + (box.width - sourceWidth * scale) * (position[0] || 0) / 100;
      const top = box.top - host.top + (box.height - sourceHeight * scale) * (position[1] || 0) / 100;
      this.classList.add('is-positioned');
      this.querySelectorAll('.mgp-hero-hotspot').forEach(marker => {
        const point = marker.querySelector('button'), card = marker.querySelector('.mgp-hero-product-card');
        const useMobile = this.dataset.mobileSource === 'true' && matchMedia('(max-width: 640px)').matches;
        const x = left + sourceWidth * scale * Number(useMobile ? marker.dataset.mx : marker.dataset.x) / 100;
        const y = top + sourceHeight * scale * Number(useMobile ? marker.dataset.my : marker.dataset.y) / 100;
        point.hidden = (useMobile && marker.dataset.hideMobile === 'true') || x < 24 || x > width - 24 || y < 24 || y > height - 24;
        point.style.left = `${x}px`; point.style.top = `${y}px`;
        if (point.hidden && marker === this.openMarker) this.close();
        if (!card.hidden) {
          const cw = card.offsetWidth, ch = card.offsetHeight;
          const fitsLeft = x - cw - 30 >= 12;
          const fitsRight = x + cw + 30 <= width - 12;
          const beside = fitsLeft || fitsRight;
          const desiredX = beside ? (fitsLeft ? x - cw - 30 : x + 30) : x - cw / 2;
          // A narrow viewport needs the card below or above the point, never on it.
          // Otherwise mouse hover can put the product link underneath an imminent click.
          const desiredY = beside ? y - ch / 2 : (y + ch + 30 <= height - 58 ? y + 30 : y - ch - 30);
          card.style.left = `${Math.max(12, Math.min(width - cw - 12, desiredX))}px`;
          card.style.top = `${Math.max(12, Math.min(height - ch - 58, desiredY))}px`;
        }
      });
    }
  });
}
